import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useDualEventHeroGsap } from "../hooks/useDualEventHeroGsap";
import "./BrandGradientButton.css";
import "./DualEventHero.css";

type DualEventEntry = {
  to: string;
  side: "london" | "australia";
  name: string;
  date: string;
  venue: string;
  cta: string;
  logoSrc: string;
  logoWidth: number;
  logoHeight: number;
  imageSrc: string;
  imageAlt: string;
};

const DUAL_EVENT_ENTRIES: DualEventEntry[] = [
  {
    to: "/xrp-australia",
    side: "australia",
    name: "XRP Australia",
    date: "18–19 March 2027",
    venue: "Crown Sydney",
    cta: "Enter Australia",
    logoSrc: "/images/xrp_sydney_2027_assets/aus_logo_no_slogan.svg",
    logoWidth: 460,
    logoHeight: 220,
    imageSrc: "/images/sydney_hero.png",
    imageAlt: "Sydney Opera House and Harbour Bridge",
  },
  {
    to: "/xrp-london",
    side: "london",
    name: "XRP London",
    date: "2–4 June 2027",
    venue: "Magazine London",
    cta: "Enter London",
    logoSrc: "/images/xrp_homepage_logo_london_no_slogan.svg",
    logoWidth: 460,
    logoHeight: 220,
    imageSrc: "/images/london_hero.png",
    imageAlt: "London skyline with Tower Bridge at dusk",
  },
];

const HOME_WAVE_SRC = "/images/xrp_sydney_2027_assets/home_page_wave_v3.png";
const HERO_VIDEO_SRC = "/videos/WOI%20Video%201%20FINAL.mp4";
const HERO_X_SRC = "/images/3D_X.png";
const PLAY_MARK_SRC = "/images/XRP%20Ledger%20-%20Symbol%20-%20Black.svg";

type NativeVideo = HTMLVideoElement & {
  webkitEnterFullscreen?: () => void;
  webkitExitFullscreen?: () => void;
};

/** Phone-sized layout, including a phone turned sideways. */
function shouldUseNativePlayer(): boolean {
  if (window.matchMedia("(max-width: 768px)").matches) return true;
  const shortSide = Math.min(window.screen.width, window.screen.height);
  return shortSide > 0 && shortSide < 768;
}

/**
 * Home hero: two full-bleed photo panels for the 2027 flagships,
 * with the main wave graphic as the divider into upcoming events.
 */
const DualEventHero: React.FC<{ id?: string }> = ({ id = "home" }) => {
  const rootRef = useRef<HTMLElement>(null);
  const playerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const playButtonRef = useRef<HTMLButtonElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const nativeRef = useRef(false);
  const fullscreenEnteredRef = useRef(false);
  const [playing, setPlaying] = useState(false);
  const [native, setNative] = useState(false);
  useDualEventHeroGsap(rootRef);

  const closeVideo = () => {
    const video = videoRef.current as NativeVideo | null;
    nativeRef.current = false;
    fullscreenEnteredRef.current = false;
    setNative(false);
    setPlaying(false);
    if (video) {
      video.pause();
      try {
        video.currentTime = 0;
      } catch {
        /* metadata not ready */
      }
      if (typeof video.webkitExitFullscreen === "function") {
        try {
          video.webkitExitFullscreen();
        } catch {
          /* not in the native iOS player */
        }
      }
    }
    if (document.fullscreenElement) {
      document.exitFullscreen?.().catch(() => {});
    }
    playButtonRef.current?.focus();
  };

  const openNativePlayer = () => {
    const video = videoRef.current as NativeVideo | null;
    const player = playerRef.current;
    if (!video) return;

    if (player) player.hidden = false;
    video.controls = true;
    video.playsInline = false;
    video.removeAttribute("playsinline");
    video.removeAttribute("webkit-playsinline");
    if (video.readyState >= 1) {
      try {
        video.currentTime = 0;
      } catch {
        /* metadata not ready */
      }
    }

    nativeRef.current = true;
    fullscreenEnteredRef.current = false;
    setNative(true);

    const playback = video.play();
    if (playback) {
      playback.catch(() => {
        nativeRef.current = false;
        fullscreenEnteredRef.current = false;
        setNative(false);
        playButtonRef.current?.focus();
      });
    }

    if (typeof video.webkitEnterFullscreen === "function") {
      try {
        video.webkitEnterFullscreen();
        return;
      } catch {
        /* standard fullscreen below */
      }
    }
    video.requestFullscreen?.().catch(() => {});
  };

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const onNativeExit = () => {
      if (!nativeRef.current) return;
      video.pause();
      try {
        video.currentTime = 0;
      } catch {
        /* metadata not ready */
      }
      nativeRef.current = false;
      fullscreenEnteredRef.current = false;
      setNative(false);
      setPlaying(false);
      playButtonRef.current?.focus();
    };

    const onFullscreenChange = () => {
      if (document.fullscreenElement) {
        fullscreenEnteredRef.current = true;
        return;
      }
      if (!fullscreenEnteredRef.current) return;
      onNativeExit();
    };

    video.addEventListener("webkitendfullscreen", onNativeExit);
    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () => {
      video.removeEventListener("webkitendfullscreen", onNativeExit);
      document.removeEventListener("fullscreenchange", onFullscreenChange);
    };
  }, []);

  useEffect(() => {
    if (!playing) return;
    const video = videoRef.current;
    if (!video) return;

    closeButtonRef.current?.focus();
    if (video.readyState >= 1) {
      video.currentTime = 0;
    }
    const playback = video.play();
    if (playback) {
      playback.catch(() => {
        setPlaying(false);
        playButtonRef.current?.focus();
      });
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeVideo();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [playing]);

  return (
    <section
      id={id}
      ref={rootRef}
      className={`dual-event-hero${playing || native ? " dual-event-hero--playing" : ""}`}
      aria-labelledby="dual-event-hero-heading"
    >
      <div className="dual-event-hero-inner" inert={playing || native ? true : undefined}>
        <h1 id="dual-event-hero-heading" className="dual-event-hero-heading">
          XRP Australia and XRP London 2027
        </h1>

        <div className="dual-event-hero-events">
          {DUAL_EVENT_ENTRIES.map((entry) => (
            <Link
              key={entry.to}
              to={entry.to}
              className={`dual-event-hero-event dual-event-hero-event--${entry.side}`}
              aria-label={`${entry.name} — ${entry.date}, ${entry.venue}. ${entry.cta}`}
            >
              <div className="dual-event-hero-event-media" aria-hidden="true">
                <img
                  src={entry.imageSrc}
                  alt=""
                  className="dual-event-hero-event-image"
                  width={1080}
                  height={1920}
                  decoding="async"
                  fetchPriority="high"
                />
              </div>

              <div className="dual-event-hero-event-content">
                <h2 className="dual-event-hero-event-name">
                  <span className="dual-event-hero-event-logo-wrap">
                    <img
                      src={entry.logoSrc}
                      alt=""
                      className={`dual-event-hero-event-logo dual-event-hero-event-logo--${entry.side}`}
                      width={entry.logoWidth}
                      height={entry.logoHeight}
                      decoding="async"
                      fetchPriority="high"
                    />
                  </span>
                </h2>

                <div className="dual-event-hero-event-meta">
                  <p className="dual-event-hero-event-date">{entry.date}</p>
                  <p className="dual-event-hero-event-venue">{entry.venue}</p>
                  <span
                    className="brand-gradient-button brand-gradient-button--cyan dual-event-hero-cta"
                    aria-hidden="true"
                  >
                    {entry.cta}
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>

        <div className="dual-event-hero-x">
          <img
            src={HERO_X_SRC}
            alt=""
            className="dual-event-hero-x-mark"
            width={1000}
            height={1000}
            decoding="async"
            fetchPriority="high"
          />
          <button
            ref={playButtonRef}
            type="button"
            className="dual-event-hero-play"
            onClick={() => {
              if (shouldUseNativePlayer()) openNativePlayer();
              else setPlaying(true);
            }}
          >
            <img
              src={PLAY_MARK_SRC}
              alt=""
              className="dual-event-hero-play-mark"
              width={211}
              height={180}
              decoding="async"
            />
            Play Video
          </button>
        </div>
      </div>

      <div
        ref={playerRef}
        className={`dual-event-hero-player${native ? " dual-event-hero-player--native" : ""}`}
        hidden={!playing && !native}
      >
        <video
          ref={videoRef}
          className="dual-event-hero-player-video"
          src={HERO_VIDEO_SRC}
          playsInline={!native}
          controls={native}
          preload="none"
          aria-label="Wave of Innovation video"
          onEnded={closeVideo}
        />
        <button
          ref={closeButtonRef}
          type="button"
          className="dual-event-hero-player-close"
          onClick={closeVideo}
        >
          Close
        </button>
      </div>

      <div className="dual-event-hero-wave" aria-hidden="true">
        <img
          src={HOME_WAVE_SRC}
          alt=""
          className="dual-event-hero-wave-img"
          width={4375}
          height={1137}
          decoding="async"
          fetchPriority="low"
        />
      </div>
    </section>
  );
};

export default DualEventHero;
